import { Flex } from '@solinkcorp/solink-components';
import { CommonContext } from 'components/Contexts';
import EventTimePicker from 'components/EventTimePicker/EventTimePicker';
import EventTimePickerDuration from 'components/EventTimePicker/EventTimePickerDuration';
import EventTimePickerRange from 'components/EventTimePicker/EventTimePickerRange';
import TimezoneNotice from 'components/TimezoneNotice/TimezoneNotice';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import * as DateHandler from 'utils/DateHandler';

interface ScheduledActivityTimePickerProps {
  timezoneId: string;
  startTime: number;
  endTime: number;
  durationLimitsMs?: readonly [number, number];
  disabled?: boolean;
  updateStartTime: (date: Date | undefined) => void;
  updateEndTime: (date: Date | undefined) => void;
  showSecond?: boolean;
  minDate: number | Date | undefined;
  maxDate: number | Date | undefined;
  errorText?: string;
  showTimezoneNotice?: boolean;
  startDateOnly?: boolean;
}

export default function ScheduledActivityTimePicker(props: ScheduledActivityTimePickerProps) {
  const {
    disabled,
    minDate,
    maxDate,
    updateStartTime,
    updateEndTime,
    timezoneId,
    startDateOnly,
    errorText,
    showTimezoneNotice,
  } = props;

  const locale = useLocale();
  const calendarStartDay = React.useContext(CommonContext).authData.firstDayOfWeek;

  const startTime = React.useMemo(() => new Date(props.startTime), [props.startTime]);
  const endTime = React.useMemo(() => new Date(props.endTime), [props.endTime]);

  return (
    <Flex flexDirection="column" gap="0.5rem" style={{ marginTop: '0.5rem' }}>
      {!!showTimezoneNotice && <TimezoneNotice timezoneId={timezoneId} />}
      {startDateOnly ? (
        <Flex width="33%">
          <EventTimePicker
            dateTimePickerProps={{
              locale,
              calendarStartDay,
              timeZoneId: timezoneId,
              value: startTime,
              onChange: updateStartTime,
              dataTestId: 'schedule-exception-start-date',
            }}
            title={locale.fields.date}
            minTime={minDate}
            maxTime={maxDate}
            hideSeconds={true}
          />
        </Flex>
      ) : (
        <Flex gap="0.5rem">
          <Flex.Item flexBasis="66%">
            <EventTimePickerRange
              disabled={disabled}
              startTime={startTime}
              endTime={endTime}
              minDate={minDate}
              maxDate={maxDate}
              updateStartTime={updateStartTime}
              updateEndTime={updateEndTime}
              timezoneId={timezoneId}
              hideSeconds={true}
              startDateTestId="schedule-exception-start-date"
              endDateTestId="schedule-exception-end-date"
            />
          </Flex.Item>
          <Flex.Item flexBasis="33%" alignSelf="stretch">
            <EventTimePickerDuration
              title={locale.cloudShare.duration}
              duration={
                startTime && endTime
                  ? DateHandler.getDuration(locale, startTime, endTime, { showSeconds: false })
                  : 'N/A'
              }
            />
          </Flex.Item>
        </Flex>
      )}
      {errorText && (
        <div className="error-text" data-testid="schedule-exception-date-error">
          {errorText}
        </div>
      )}
    </Flex>
  );
}
